import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Activity, Brain, Zap, Database, Play, Download,
  Stethoscope, Scale, TrendingUp, Users, Shield,
  ArrowRight, ChevronRight, Copy, Check,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.08, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

const VERTICALS = [
  {
    icon: Stethoscope,
    label: "Healthcare",
    desc: "Surface clinical accuracy flags and compliance signals in real time",
  },
  {
    icon: Scale,
    label: "Legal",
    desc: "Detect argument challenges and fact claims during depositions or negotiations",
  },
  {
    icon: TrendingUp,
    label: "Financial Services",
    desc: "Flag objections and data inconsistencies across client advisory calls",
  },
  {
    icon: Users,
    label: "Recruiting",
    desc: "Track candidate responses and identify follow-up opportunities live",
  },
  {
    icon: Shield,
    label: "Insurance",
    desc: "Detect negotiation tension and policy fact-check moments automatically",
  },
];

const API_PRIMITIVES = [
  {
    method: "POST",
    path: "/stream.connect",
    icon: Play,
    desc: "Connect a real-time audio stream or transcript feed to the intelligence engine",
  },
  {
    method: "POST",
    path: "/intelligence.configure",
    icon: Brain,
    desc: "Define which capabilities run, their thresholds, and output format for your tenant",
  },
  {
    method: "GET",
    path: "/signals.subscribe",
    icon: Activity,
    desc: "Subscribe to the real-time signal stream via SSE — receive cards as they're generated",
  },
  {
    method: "GET",
    path: "/memory.query",
    icon: Database,
    desc: "Query the conversation graph for a person, deal, or context across sessions",
  },
  {
    method: "POST",
    path: "/actions.trigger",
    icon: Zap,
    desc: "Trigger downstream actions — CRM writes, webhooks, notifications — from any signal",
  },
  {
    method: "GET",
    path: "/insights.export",
    icon: Download,
    desc: "Export structured conversation data, signal history, and outcomes for any time window",
  },
];

const CODE_SNIPPET = `from shadow_platform import ShadowPlatform

client = ShadowPlatform(
    api_key="sk_live_...",
    tenant_id="acme-health",
)

# Configure which capabilities run for your tenant
client.intelligence_configure({
    "enabled_capabilities": ["fact_check", "clinical_flag"],
    "thresholds": {"default_confidence": 0.5},
    "prompt_overrides": {
        "fact_check": {
            "system_instruction": "You are a clinical accuracy checker..."
        }
    }
})

# Subscribe to real-time signals
async for signal in client.signals_subscribe(session_id="sess_123"):
    print(signal)  # { card_type: "clinical_flag", content: {...} }`;

const USE_CASES = [
  "Healthcare",
  "Legal",
  "Financial Services",
  "Recruiting",
  "Insurance",
  "Other",
];

function CodeBlock({ code, language = "python" }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative rounded-xl overflow-hidden border border-gray-800">
      <div className="flex items-center justify-between px-4 py-2 bg-gray-950 border-b border-gray-800">
        <span className="text-xs text-gray-500 font-mono">{language}</span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-300 transition-colors"
        >
          {copied ? <Check size={13} /> : <Copy size={13} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="bg-gray-900 text-gray-100 text-sm leading-relaxed p-5 overflow-x-auto font-mono">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function WaitlistForm({ formRef }) {
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [useCase, setUseCase] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/platform/waitlist`,
        { email, company: company || null, use_case: useCase || null },
        { withCredentials: false }
      );
      setResult(res.data.status);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail[0]?.msg || "Please enter a valid email address.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (result === "joined") {
    return (
      <div className="text-center py-8">
        <div className="w-12 h-12 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-4">
          <Check className="text-teal-600" size={22} />
        </div>
        <p className="text-lg font-semibold text-gray-900">You're on the list.</p>
        <p className="text-gray-500 mt-1 text-sm">We'll be in touch with early API access details.</p>
      </div>
    );
  }

  if (result === "already_registered") {
    return (
      <div className="text-center py-8">
        <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
          <Check className="text-blue-500" size={22} />
        </div>
        <p className="text-lg font-semibold text-gray-900">Already registered.</p>
        <p className="text-gray-500 mt-1 text-sm">We'll reach out soon with your early access details.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Work email <span className="text-teal-600">*</span>
        </label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Company <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <input
          type="text"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          placeholder="Acme Corp"
          className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Use case <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <select
          value={useCase}
          onChange={(e) => setUseCase(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition bg-white"
        >
          <option value="">Select your vertical</option>
          {USE_CASES.map((uc) => (
            <option key={uc} value={uc}>{uc}</option>
          ))}
        </select>
      </div>
      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3.5 py-2.5">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white font-medium text-sm py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2"
      >
        {submitting ? "Submitting…" : (
          <>Get early API access <ArrowRight size={15} /></>
        )}
      </button>
    </form>
  );
}

export default function PlatformLandingPage() {
  const waitlistRef = useRef(null);

  const scrollToWaitlist = (e) => {
    e.preventDefault();
    waitlistRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-sm z-50 border-b border-gray-100">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-3 h-3 bg-black rounded-full" />
            <span className="text-lg font-semibold tracking-tight">
              <span className="text-gray-800">SHADOW</span>
              <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
                LABS
              </span>
            </span>
          </Link>
          <div className="flex items-center gap-6">
            <Link
              to="/platform/docs"
              className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
            >
              API Docs
            </Link>
            <button
              onClick={scrollToWaitlist}
              className="text-sm font-medium bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Join Waitlist
            </button>
          </div>
        </nav>
      </header>

      <main className="pt-24">
        {/* Hero */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <span className="inline-block text-xs tracking-widest uppercase text-teal-600 font-medium mb-4 border border-teal-100 bg-teal-50 px-3 py-1 rounded-full">
              Platform API — Early Access
            </span>
          </motion.div>
          <motion.h1
            initial="hidden"
            animate="visible"
            custom={1}
            variants={fadeUp}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 leading-tight mt-4"
          >
            Build real-time conversation
            <br className="hidden sm:block" />
            <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
              {" "}intelligence
            </span>{" "}
            into any product
          </motion.h1>
          <motion.p
            initial="hidden"
            animate="visible"
            custom={2}
            variants={fadeUp}
            className="mt-6 text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed"
          >
            Shadow's intelligence stack — fact-check, objection handling, signal detection —
            available as a configurable API for any vertical. Healthcare, legal, financial
            services, recruiting, insurance.
          </motion.p>
          <motion.div
            initial="hidden"
            animate="visible"
            custom={3}
            variants={fadeUp}
            className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3"
          >
            <Link
              to="/platform/docs"
              className="flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors"
            >
              View API Docs <ChevronRight size={15} />
            </Link>
            <button
              onClick={scrollToWaitlist}
              className="flex items-center gap-2 border border-teal-200 text-teal-700 hover:bg-teal-50 text-sm font-medium px-5 py-2.5 rounded-lg transition-colors"
            >
              Join Waitlist <ArrowRight size={15} />
            </button>
          </motion.div>
        </section>

        {/* Verticals */}
        <section className="bg-gray-50 py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.p
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="text-xs tracking-widest uppercase text-gray-400 font-medium text-center mb-10"
            >
              Built for high-stakes conversations
            </motion.p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {VERTICALS.map((v, i) => (
                <motion.div
                  key={v.label}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  custom={i}
                  variants={fadeUp}
                  className="bg-white rounded-xl p-5 border border-gray-100 hover:border-teal-200 hover:shadow-sm transition-all"
                >
                  <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center mb-3">
                    <v.icon size={18} className="text-teal-600" />
                  </div>
                  <p className="text-sm font-semibold text-gray-900 mb-1.5">{v.label}</p>
                  <p className="text-xs text-gray-500 leading-relaxed">{v.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* API Primitives */}
        <section className="py-20 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-center mb-12"
          >
            <p className="text-xs tracking-widest uppercase text-teal-600 font-medium mb-3">
              API Surface
            </p>
            <h2 className="text-3xl font-bold text-gray-900 tracking-tight">
              Six primitives. Any conversation.
            </h2>
            <p className="text-gray-500 mt-3 text-base max-w-xl mx-auto">
              A minimal, composable API designed around how conversations actually work.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {API_PRIMITIVES.map((p, i) => (
              <motion.div
                key={p.path}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
                className="rounded-xl border border-gray-100 p-5 hover:border-teal-200 hover:shadow-sm transition-all"
              >
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-gray-900 flex items-center justify-center flex-shrink-0">
                    <p.icon size={14} className="text-white" />
                  </div>
                  <div className="min-w-0">
                    <span className="text-xs font-mono font-medium text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded">
                      {p.method}
                    </span>
                    <p className="text-xs font-mono text-gray-900 mt-0.5 truncate">{p.path}</p>
                  </div>
                </div>
                <p className="text-sm text-gray-500 leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={6}
            variants={fadeUp}
            className="text-center mt-8"
          >
            <Link
              to="/platform/docs"
              className="text-sm text-teal-600 hover:text-teal-700 font-medium inline-flex items-center gap-1.5 transition-colors"
            >
              Full API reference <ArrowRight size={14} />
            </Link>
          </motion.div>
        </section>

        {/* Code Snippet */}
        <section className="bg-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="text-center mb-8"
            >
              <p className="text-xs tracking-widest uppercase text-gray-400 font-medium mb-3">
                Get started in minutes
              </p>
              <h2 className="text-2xl font-bold text-gray-900 tracking-tight">
                Configure once. Intelligence everywhere.
              </h2>
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={1}
              variants={fadeUp}
            >
              <CodeBlock code={CODE_SNIPPET} language="python" />
            </motion.div>
          </div>
        </section>

        {/* Waitlist Form */}
        <section
          id="waitlist"
          ref={waitlistRef}
          className="py-20 max-w-lg mx-auto px-4 sm:px-6 lg:px-8"
        >
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-center mb-8"
          >
            <p className="text-xs tracking-widest uppercase text-teal-600 font-medium mb-3">
              Early Access
            </p>
            <h2 className="text-3xl font-bold text-gray-900 tracking-tight">
              Get early API access
            </h2>
            <p className="text-gray-500 mt-3 text-sm">
              We're onboarding a small group of design partners. Tell us what you're building.
            </p>
          </motion.div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={1}
            variants={fadeUp}
            className="bg-white border border-gray-100 rounded-2xl p-6 sm:p-8 shadow-sm"
          >
            <WaitlistForm formRef={waitlistRef} />
          </motion.div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-8 text-center">
        <p className="text-sm text-gray-400">
          © 2026 ShadowLabs{" "}
          <span className="mx-2 text-gray-200">·</span>
          <Link to="/privacy" className="hover:text-gray-600 transition-colors">Privacy</Link>
          <span className="mx-2 text-gray-200">·</span>
          <Link to="/terms" className="hover:text-gray-600 transition-colors">Terms</Link>
          <span className="mx-2 text-gray-200">·</span>
          <Link to="/" className="hover:text-gray-600 transition-colors">Back to app</Link>
        </p>
      </footer>
    </div>
  );
}
